import { create } from "zustand";
import * as SharedMessageTypes from "../../darvin-shared/schema/zod/agent/message-types.schema";
import { useProjectStore } from "../core_stores/useProjectStore";
import {
  ProjectChatAssistantMessage,
  ProjectChatElement,
  ProjectChatUserMessage,
} from "./chat_messages/chatModels";
import { useChatMessagesStore } from "./chat_messages/useChatMessagesStore";

interface TechnicalErrorHandlerActions {
  initialize: () => void;
  rollback: (newPrompt: string | null, newImage: File | null) => Promise<void>;
  dispose: () => void;
}

interface TechnicalErrorHandlerState {
  canHandleTechnicalError: boolean;

  actions: TechnicalErrorHandlerActions;
}

const useTechnicalErrorHandlerStore = create<TechnicalErrorHandlerState>(
  (set) => {
    let unsubscribeChatElements: (() => void) | null = null;
    let isRollingBack: boolean = false;
    let prompt: string | null = null;
    let image: File | null = null;

    function canHandleTechnicalError(
      chatElements: ProjectChatElement[]
    ): boolean {
      if (chatElements.length === 0) {
        return false;
      }
      const lastMessage = chatElements[chatElements.length - 1];
      const isLastMsgTechnicalError =
        lastMessage instanceof ProjectChatAssistantMessage &&
        lastMessage.type === "error" &&
        (lastMessage.value as SharedMessageTypes.ErrorContent).type ===
          SharedMessageTypes.ErrorTypeEnum.Enum.technical;
      return (
        isLastMsgTechnicalError && hasLastUserMsgCheckpointId(chatElements)
      );
    }

    function hasLastUserMsgCheckpointId(
      chatElements: ProjectChatElement[]
    ): boolean {
      const lastUserMsg = [...(chatElements ?? [])]
        .reverse()
        .find((msg) => msg instanceof ProjectChatUserMessage);
      return lastUserMsg?.checkpointId !== undefined;
    }

    async function sendMessage() {
      useChatMessagesStore
        .getState()
        .actions.addTemporaryMessage(prompt!, image);
      await useProjectStore.getState().actions.sendMessage(prompt!, image);
    }

    return {
      canHandleTechnicalError: false,

      actions: {
        initialize: () => {
          unsubscribeChatElements = useChatMessagesStore.subscribe((state) => {
            set({
              canHandleTechnicalError: canHandleTechnicalError(
                state.chatElements ?? []
              ),
            });
          });
        },

        rollback: async (newPrompt: string | null, newImage: File | null) => {
          isRollingBack = true;
          prompt = newPrompt;
          image = newImage;
          await useProjectStore.getState().actions.rollback();
          if (prompt) {
            await new Promise<void>((resolve, reject) => {
              const unsubscribe = useProjectStore.subscribe(
                (state) => state.latestMsg,
                async (latestMsg) => {
                  if (
                    latestMsg?.type ===
                    SharedMessageTypes.MessageTypeEnum.Enum.jobDone
                  ) {
                    unsubscribe();
                    try {
                      await sendMessage();
                      resolve();
                    } catch (error) {
                      reject(error);
                    }
                  }
                }
              );
            });
          }
          isRollingBack = false;
          prompt = null;
          image = null;
        },

        dispose: () => {
          if (unsubscribeChatElements) {
            unsubscribeChatElements();
            unsubscribeChatElements = null;
          }
          set({ canHandleTechnicalError: false });
          isRollingBack = false;
          prompt = null;
          image = null;
        },
      },
    };
  }
);

export const useCanHandleTechnicalError = () =>
  useTechnicalErrorHandlerStore((state) => state.canHandleTechnicalError);

export const useTechnicalErrorHandlerActions = () =>
  useTechnicalErrorHandlerStore((state) => state.actions);
